import type {
  WorldCupGroupStageFixture,
  WorldCupGroupStageTable,
  WorldCupNationalTeamStandingRow,
} from '../types/worldCupTournamentStandings.types';
import { WORLD_CUP_GROUP_STAGE_LETTERS } from '../constants/worldCupGroupStageLetters.constants';

/** Only A–L tables are real groups; ignore pseudo-tables (e.g. third-placed ranking) for match-card positions. */
const WORLD_CUP_LETTER_GROUP_SET = new Set<string>(WORLD_CUP_GROUP_STAGE_LETTERS);

function isGroupStageFixtureComplete(f: WorldCupGroupStageFixture): boolean {
  return f.homeScore !== undefined && f.awayScore !== undefined;
}

export interface ComputedGroupStandings {
  teams: WorldCupNationalTeamStandingRow[];
  hasResults: boolean;
}

/**
 * Build standings for one letter group from fixtures (completed matches update P/W/D/L/GF/GA/Pts).
 */
export function computeStandingsFromGroupFixtures(
  groupLetter: string,
  fixtures: readonly WorldCupGroupStageFixture[],
): ComputedGroupStandings {
  const groupFixtures = fixtures.filter((m) => m.groupLetter === groupLetter);
  const teams: Record<string, WorldCupNationalTeamStandingRow> = {};

  const ensure = (name: string, countryCode: string) => {
    if (!teams[countryCode]) {
      teams[countryCode] = {
        name,
        countryCode,
        played: 0,
        won: 0,
        drawn: 0,
        lost: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        goalDifference: 0,
        points: 0,
      };
    }
  };

  groupFixtures.forEach((m) => {
    ensure(m.homeTeam, m.homeCountryCode);
    ensure(m.awayTeam, m.awayCountryCode);
  });

  let hasResults = false;
  groupFixtures.forEach((m) => {
    if (!isGroupStageFixtureComplete(m)) return;
    hasResults = true;
    const home = m.homeScore as number;
    const away = m.awayScore as number;
    const ht = teams[m.homeCountryCode];
    const at = teams[m.awayCountryCode];
    ht.played += 1;
    at.played += 1;
    ht.goalsFor += home;
    ht.goalsAgainst += away;
    at.goalsFor += away;
    at.goalsAgainst += home;

    if (home > away) {
      ht.won += 1;
      ht.points += 3;
      at.lost += 1;
    } else if (home < away) {
      at.won += 1;
      at.points += 3;
      ht.lost += 1;
    } else {
      ht.drawn += 1;
      at.drawn += 1;
      ht.points += 1;
      at.points += 1;
    }
  });

  const list = Object.values(teams)
    .map((t) => ({ ...t, goalDifference: t.goalsFor - t.goalsAgainst }))
    .sort(compareGroupStageStandings);

  return { teams: list, hasResults };
}

function compareGroupStageStandings(
  a: WorldCupNationalTeamStandingRow,
  b: WorldCupNationalTeamStandingRow,
): number {
  if (b.points !== a.points) return b.points - a.points;
  if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
  if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
  return a.name.localeCompare(b.name);
}

/**
 * Builds a countryCode → { groupLetter, position } lookup map from tournament standings.
 * Used by the picks adapter hook to annotate group stage matches with team positions.
 */
export function buildGroupPositionMap(
  groups: readonly WorldCupGroupStageTable[],
): Record<string, { groupLetter: string; position: number }> {
  const map: Record<string, { groupLetter: string; position: number }> = {};
  for (const group of groups) {
    if (!WORLD_CUP_LETTER_GROUP_SET.has(group.groupLetter)) {
      continue;
    }
    group.teams.forEach((team, idx) => {
      map[team.countryCode] = { groupLetter: group.groupLetter, position: idx + 1 };
    });
  }
  return map;
}
